import { useCallback, useState } from 'react';

import { useTranslation } from '@/i18n';
import { monthlyCutsClosureService, mapMonthlyCutsClosureSummary } from '@/api/domains/attendance';
import type { MonthlyCutsClosureSummary } from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';


export interface UseCloseMonthlyCutsResult {
  closeCuts: (monthlyCutIds: number[]) => Promise<MonthlyCutsClosureSummary | null>;
  isClosing: boolean;
}

// Cierra uno o varios cortes mensuales. Muestra un resumen por toast (éxito total cuando todos se
// cerraron, advertencia parcial cuando hubo cortes omitidos por ya estar cerrados o no encontrados)
// y delega el refresco del listado/limpieza de selección al llamador.
export function useCloseMonthlyCuts(): UseCloseMonthlyCutsResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning } = useUI();
  const { createSignal } = useAbortController();

  const [isClosing, setIsClosing] = useState(false);

  const closeCuts = useCallback(
    async (monthlyCutIds: number[]): Promise<MonthlyCutsClosureSummary | null> => {
      if (monthlyCutIds.length === 0) return null;

      setIsClosing(true);
      try {
        const response = await monthlyCutsClosureService.close(
          { monthlyCutIds },
          createSignal(),
        );

        if (response.error?.code === 'ABORTED') return null;
        if (!response.success || !response.data) {
          showApiError(response, translate('attendance:monthlyCutsList.closure.messages.closeError'));
          return null;
        }

        const summary = mapMonthlyCutsClosureSummary(response.data);
        const hasIssues = summary.skippedIds.length > 0 || summary.notFoundIds.length > 0;

        if (!hasIssues) {
          showSuccess(
            translate('attendance:monthlyCutsList.closure.messages.closeSuccess', {
              count: summary.closedIds.length,
            }),
          );
        } else {
          showWarning(
            translate('attendance:monthlyCutsList.closure.messages.closePartial', {
              closed: summary.closedIds.length,
              skipped: summary.skippedIds.length,
              notFound: summary.notFoundIds.length,
            }),
          );
        }

        return summary;
      } finally {
        setIsClosing(false);
      }
    },
    [createSignal, showApiError, showSuccess, showWarning, translate],
  );

  return { closeCuts, isClosing };
}

export default useCloseMonthlyCuts;
